// Third party imports
import * as React from 'react';
import { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import { Text, Field, ImageField, RichText, LinkField } from '@sitecore-jss/sitecore-jss-nextjs';
import lowerCase from 'lodash/lowerCase';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import getCallCenterNumber from '../../../store/selectors/getCallCenterNumber';
import pnMergetagReplacer from '../../../lib/utils/pnMergeTagReplacer';

// Project specific imports
import { ComponentProps } from 'lib/component-props';
import { ButtonType, IButton } from '../../../lib/type/Button';
import { HeroBannerType } from './DTPHeroBanner.type';
import { isEditorActive } from '@sitecore-jss/sitecore-jss-nextjs/utils';
import { JOURNEYS } from '../../../constants';
import {
  StBannerContainer,
  StBannerContent,
  StBannerWrapper,
  StButtonStack,
  StOrText,
  StRichTextGridItem,
} from './DTPHeroBanner.styles';
import { RootState } from '../../../store/store';
import { useSelector } from 'react-redux';
import CustomNextImage from '../../shared/CustomNextImage/CustomNextImage';
import { trackEvent } from '../../../lib/utils/gtmService';
import * as cdpService from '../../../services/CdpService';
import { CDP_EVENTS } from '../../../../src/constants';
import isClinicalTrialSite from '../../../lib/utils/siteHelper';
import UiHeading from '../../../components/shared/Heading';
import ButtonPrimary from '../../../components/shared/Button/PrimaryButton';
import ButtonSecondary from '../../../components/shared/Button/SecondaryButton';
import UiBox from '../../../components/shared/Box';
import useApplyV2Theme from '../../../lib/hooks/useApplyV2Theme';
import { Typography } from '@mui/material';

type DTPHeroBannerProps = ComponentProps & {
  fields: {
    title: Field<string>;
    image: ImageField;
    content: Field<string>;
    ctaButtons: IButton[];
    componentVariantType: { value: string };
    orText: Field<string>;
    imageDesktop: ImageField;
    imageMobile: ImageField;
    imageTablet: ImageField;
  };
};
const fallbackLink: LinkField = { value: { href: '', text: '' } };
const DTPHeroBanner = (props: DTPHeroBannerProps): JSX.Element => {
  const theme = useTheme();
  const { isToapplyV2Theme } = useApplyV2Theme();
  const { sitecoreContext } = useSitecoreContext();
  const callCenterNumber = getCallCenterNumber(sitecoreContext);
  const siteSettings: any = sitecoreContext?.siteSettings;
  const isScreenerLandingPageEnabled = siteSettings?.enableScreenerLandingPage?.value;
  const journeyType = useSelector((state: RootState) => state?.cdpData?.journeyType);
  const isJourneyType3: boolean = journeyType === JOURNEYS.JOURNEY_03;
  const { fields } = props || {};
  const isNotificationBannerOpen = useSelector(
    (state: RootState) => state.config.isNotificationBannerOpen
  );
  // Setting default variant as V1. Note that content should be managed
  // by Author accordingly
  const variantType = fields?.componentVariantType?.value
    ? lowerCase(fields?.componentVariantType?.value).split(' ').join('')
    : HeroBannerType.V1;
  const isVariant1 = variantType === HeroBannerType.V1;
  const ctaButtonsField = fields?.ctaButtons;
  const [buttonsArr, setButtonsArr] = useState<IButton[]>([]);
  const siteName = sitecoreContext?.site?.name;

  const scrollToSection = (e: any) => {
    trackEvents();
    if (e) {
      const itemId = e.split('#')[1];
      const itemOffset = document.getElementById(itemId)?.offsetTop;
      const margin = isNotificationBannerOpen ? 50 : 0;
      if (itemOffset) {
        window.scroll({
          top: itemOffset - margin,
          left: 0,
          behavior: 'smooth',
        });
      }
    }
  };
  const trackEvents = () => {
    trackEvent(
      {
        event: 'Hero CTA Clicked',
      },
      isClinicalTrialSite(siteName as string) ? true : false
    );
    cdpService.event(CDP_EVENTS.SCREENER_CTA_LINK_CLICKED, {
      tenantKey: siteSettings?.tenantKey?.value,
      language: sitecoreContext?.language,
    });
  };

  const handleOnClick = (ctaButtonHref: any) => {
    if (ctaButtonHref) {
      window.open(ctaButtonHref, '_self', 'noopener,noreferrer');
    }
  };

  useEffect(() => {
    if (Array.isArray(ctaButtonsField)) {
      const newButtons = ctaButtonsField.map((item) => {
        const isCallCenter = item.fields.callCenterCTAButton?.value;
        // if `isCallCenter` property is present its a CallCenter CTA button
        if (typeof isCallCenter !== 'undefined' && item.fields.link) {
          const href = `tel:${callCenterNumber.value || ''}`;
          const text = pnMergetagReplacer(
            item.fields.link.value.text,
            String(callCenterNumber.value || ''),
            false
          );
          item.fields.link.value.href = href;
          item.fields.link.value.text = text;
          return item;
        }
        return item;
      });
      setButtonsArr(newButtons);
    }
  }, [callCenterNumber.value]);

  if (!props?.fields) return <></>;

  return (
    <StBannerWrapper id="dtp-hero-banner">
      {!isEditorActive() && (
        <CustomNextImage
          id="dtp-hero-banner-background-image"
          priority={true} // When true, the image will be considered high priority and preload
          style={{ zIndex: 0 }}
          layout="fill"
          objectFit="cover"
          objectPosition="center"
          mobileImg={fields?.imageMobile}
          tabletImg={fields?.imageTablet}
          desktopImg={fields?.imageDesktop}
        />
      )}
      <StBannerContainer
        id="hero-banner"
        container
        variant={variantType}
        style={isEditorActive() ? { backgroundImage: `url(${fields?.image?.value?.src})` } : {}}
      >
        <StBannerContent container item sx={{ zIndex: 1 }}>
          <Grid item>
            {isToapplyV2Theme ? (
              <UiHeading size="h1" data-testid="hero-banner-title" isChildren>
                <RichText field={fields?.title} />
              </UiHeading>
            ) : (
              <Typography
                className="hero-banner-title"
                gutterBottom
                variant="h1"
                sx={{
                  fontSize: '2rem',
                  marginBottom: '8px',
                  [theme.breakpoints.down('md')]: {
                    fontSize: '2rem',
                  },
                  [theme.breakpoints.down('sm')]: {
                    fontSize: '1.875rem',
                  },
                }}
              >
                <RichText field={fields?.title} />
              </Typography>
            )}
          </Grid>
          <StRichTextGridItem item variant={variantType}>
            <RichText field={fields?.content} />
          </StRichTextGridItem>
        </StBannerContent>
        <Grid
          item
          sx={{
            marginTop: '22px',
            zIndex: 1,
            [theme.breakpoints.down('sm')]: {
              width: '100%',
            },
          }}
        >
          <StButtonStack spacing={2}>
            {buttonsArr.map((ctaButton: IButton, index: number) => {
              const btnType: string =
                ctaButton?.fields?.buttonVariant?.value || ButtonType.CONTAINED;
              const ctaButtonLink = isJourneyType3
                ? ctaButton?.fields?.questionnaireCtaLink
                : ctaButton?.fields?.screenerCtaLink;
              const ctaButtonHref = ctaButton?.fields?.link?.value?.href;
              if (lowerCase(btnType) === ButtonType.CONTAINED) {
                if (!isScreenerLandingPageEnabled) {
                  return (
                    <>
                      <UiBox styles={{ width: '100%', minWidth: '205px' }}>
                        <ButtonPrimary
                          id={ctaButton?.name}
                          className={'hero-cta-btn btn'}
                          key={ctaButton?.id}
                          onClick={() => scrollToSection(ctaButtonHref)}
                          fullWidth
                          aria-label={
                            ctaButton?.fields?.ariaLabel?.value ||
                            ctaButton?.fields?.link?.value?.text
                          }
                        >
                          {ctaButton?.fields?.link?.value?.text || fallbackLink.value.text}
                        </ButtonPrimary>
                      </UiBox>
                      {index === 0 &&
                        buttonsArr.length > 1 &&
                        (props?.fields?.orText?.value != null ||
                          props?.fields?.orText?.value != '' ||
                          props?.fields?.orText?.value != undefined) && (
                          <StOrText spacing={2}>
                            <Text field={props?.fields?.orText} />
                          </StOrText>
                        )}
                    </>
                  );
                }
                return (
                  <UiBox styles={{ width: '100%', minWidth: '205px' }} key={ctaButton?.id}>
                    <ButtonPrimary
                      id={ctaButton?.name}
                      className={'hero-cta-btn btn'}
                      key={ctaButton?.id}
                      fullWidth
                      onClick={() => handleOnClick(ctaButtonHref)}
                    >
                      {ctaButtonLink?.value?.text || fallbackLink.value.text}
                    </ButtonPrimary>
                  </UiBox>
                );
              } else if (btnType === ButtonType.OUTLINED) {
                if (isVariant1) {
                  return (
                    <>
                      <UiBox styles={{ width: '100%', minWidth: '205px' }} key={ctaButton?.id}>
                        <ButtonSecondary
                          id={ctaButton?.name}
                          key={ctaButton?.id}
                          aria-label={
                            ctaButton?.fields?.ariaLabel?.value ||
                            ctaButton?.fields?.link?.value?.text
                          }
                          fullWidth
                          onClick={() => handleOnClick(ctaButtonHref)}
                        >
                          {ctaButton?.fields?.link?.value?.text || fallbackLink.value.text}
                        </ButtonSecondary>
                      </UiBox>
                      {index === 0 &&
                        buttonsArr.length > 1 &&
                        (props?.fields?.orText?.value != null ||
                          props?.fields?.orText?.value != '' ||
                          props?.fields?.orText?.value != undefined) && (
                          <StOrText spacing={2}>
                            <Text field={props?.fields?.orText} />
                          </StOrText>
                        )}
                    </>
                  );
                }
                return (
                  <>
                    <UiBox styles={{ width: '100%', minWidth: '205px' }} key={ctaButton?.id}>
                      <ButtonSecondary
                        id={ctaButton?.name}
                        className={'btn'}
                        key={ctaButton?.id}
                        aria-label={
                          ctaButton?.fields?.ariaLabel?.value ||
                          ctaButton?.fields?.link?.value?.text
                        }
                        fullWidth
                        onClick={() => handleOnClick(ctaButtonHref)}
                      >
                        {ctaButton?.fields?.link?.value?.text || fallbackLink.value.text}
                      </ButtonSecondary>
                    </UiBox>
                    {index === 0 &&
                      buttonsArr.length > 1 &&
                      (props?.fields?.orText?.value != null ||
                        props?.fields?.orText?.value != '' ||
                        props?.fields?.orText?.value != undefined) && (
                        <StOrText spacing={2}>
                          <Text field={props?.fields?.orText} />
                        </StOrText>
                      )}
                  </>
                );
              }
              return (
                <>
                  <UiBox styles={{ width: '100%', minWidth: '205px' }} key={ctaButton?.id}>
                    <ButtonSecondary
                      id={ctaButton?.name}
                      className={'btn'}
                      key={ctaButton?.id}
                      aria-label={
                        ctaButton?.fields?.ariaLabel?.value || ctaButton?.fields?.link?.value?.text
                      }
                      fullWidth
                      onClick={() => handleOnClick(ctaButtonHref)}
                    >
                      {ctaButton?.fields?.link?.value?.text || fallbackLink.value.text}
                    </ButtonSecondary>
                  </UiBox>
                  {index === 0 &&
                    buttonsArr.length > 1 &&
                    (props?.fields?.orText?.value != null ||
                      props?.fields?.orText?.value != '' ||
                      props?.fields?.orText?.value != undefined) && (
                      <StOrText spacing={2}>
                        <Text field={props?.fields?.orText} />
                      </StOrText>
                    )}
                </>
              );
            })}
          </StButtonStack>
        </Grid>
      </StBannerContainer>
    </StBannerWrapper>
  );
};

export default DTPHeroBanner;
